import React from 'react';
import { cn } from '../../utils/cn';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'locked' | 'unlocked' | 'warning' | 'success';
  isLocked?: boolean;
  lockMessage?: string;
}

export function Card({
  className,
  variant = 'default',
  isLocked = false,
  lockMessage = '🔒 Locked',
  children,
  ...props
}: CardProps) {
  const baseClasses = 'rounded-lg p-4 shadow-sm border border-outline';

  const variantClasses = {
    default: 'bg-surface text-on-surface',
    locked: 'bg-surface text-on-surface opacity-70',
    unlocked: 'bg-surface text-on-surface',
    warning: 'bg-error-container text-on-error-container',
    success: 'bg-primary-container text-on-primary-container',
  };

  if (isLocked) {
    return (
      <div className={cn(baseClasses, variantClasses[variant], 'relative', className)} {...props}>
        <div className="absolute inset-0 flex items-center justify-center bg-surface/95 backdrop-blur-sm z-10 rounded-lg">
          <div className="text-center p-6">
            {lockMessage}
            {children}
          </div>
        </div>
        <div className="opacity-50">{children}</div>
      </div>
    );
  }

  return (
    <div className={cn(baseClasses, variantClasses[variant], className)} {...props}>
      {children}
    </div>
  );
}

export function LockedCard({ className, lockMessage, children, ...props }: CardProps) {
  return <Card variant="locked" isLocked={true} lockMessage={lockMessage} className={className} {...props} />;
}

export function UnlockedCard({ className, children, ...props }: CardProps) {
  return <Card variant="unlocked" className={className} {...props} />;
}

export function WarningCard({ className, children, ...props }: CardProps) {
  return <Card variant="warning" className={className} {...props} />;
}

export function SuccessCard({ className, children, ...props }: CardProps) {
  return <Card variant="success" className={className} {...props} />;
}
